import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { CONFIG } from '../constants/config';
import type {
  AuthTokens,
  Guard,
  PortalUser,
  PatrolSession,
  PatrolPoint,
  LiveSession,
  HistoryTrack,
  AlarmEvent,
  IncidentEvent,
  Estate,
  Paginated,
  PatrolType,
  MonthlyReport,
  Drone,
} from '../types';

// ── Axios instance ─────────────────────────────────────────────────────────────

const api: AxiosInstance = axios.create({
  baseURL: CONFIG.API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach access token to every request
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401: attempt token refresh, then replay the request once
let refreshingPromise: Promise<string> | null = null;

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Read-only demo: explain the refusal once, here, rather than leaving
    // every caller to render a bare "failed" message. Catches any action that
    // isn't visibly disabled, including ones added later.
    if (err.response?.status === 403 && err.response?.data?.error === 'demo_read_only') {
      window.alert(
        'This is a read-only demo, so changes are disabled.\n\n' +
        'Everything else works normally — the patrollers and drones you can see are live simulations.'
      );
      return Promise.reject(err);
    }

    if (err.response?.status !== 401 || original._retry) {
      return Promise.reject(err);
    }
    original._retry = true;

    const { refreshToken, setTokens, clearTokens } = useAuthStore.getState();
    if (!refreshToken) {
      clearTokens();
      return Promise.reject(err);
    }

    if (!refreshingPromise) {
      refreshingPromise = axios
        .post<AuthTokens>(`${CONFIG.API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken })
        .then((r) => {
          // The server rotates the refresh token (old one is consumed) —
          // store BOTH tokens or the next refresh fails and logs the user out.
          setTokens(r.data.access_token, r.data.refresh_token);
          return r.data.access_token;
        })
        .catch((e) => {
          clearTokens();
          throw e;
        })
        .finally(() => {
          refreshingPromise = null;
        });
    }

    const newToken = await refreshingPromise;
    original.headers.Authorization = `Bearer ${newToken}`;
    return api(original);
  },
);

// ── Auth ───────────────────────────────────────────────────────────────────────

export const authApi = {
  login(email: string, password: string) {
    return api.post<AuthTokens>('/auth/login', { email, password }).then((r) => r.data);
  },
  refresh(refreshToken: string) {
    return api.post<AuthTokens>('/auth/refresh', { refresh_token: refreshToken }).then((r) => r.data);
  },
  /**
   * Ends THIS browser's sign-in only (2026-10-12): the server deletes the
   * refresh token named here and leaves every other device signed in.
   */
  logout() {
    const { refreshToken } = useAuthStore.getState();
    return api.post('/auth/logout', { refresh_token: refreshToken ?? null }).then(() => undefined);
  },
  updateMe(data: { display_name?: string; password?: string; current_password?: string }) {
    return api.patch('/me', data).then((r) => r.data);
  },
  me() {
    return api.get<PortalUser & { read_only?: boolean }>('/me').then((r) => r.data);
  },
  /** Always resolves, whether or not the address exists — the server
   *  deliberately does not reveal which addresses are registered. */
  forgotPassword(email: string) {
    return api.post('/auth/forgot-password', { email }).then(() => undefined);
  },
  resetPassword(token: string, password: string) {
    return api.post('/auth/reset-password', { token, password }).then(() => undefined);
  },
};

// ── Patrols ────────────────────────────────────────────────────────────────────

export const patrolApi = {
  list(page = 1, guardId?: string) {
    const params: Record<string, unknown> = { page, per_page: CONFIG.PAGINATION_PAGE_SIZE };
    if (guardId) params.guard_id = guardId;
    return api
      .get<Paginated<PatrolSession>>('/patrols', { params })
      .then((r) => r.data);
  },
  get(id: string) {
    return api.get<PatrolSession>(`/patrols/${id}`).then((r) => r.data);
  },
  track(id: string) {
    return api.get<PatrolPoint[]>(`/patrols/${id}/track`).then((r) => r.data);
  },
  alarms(id: string) {
    return api.get<AlarmEvent[]>(`/patrols/${id}/alarms`).then((r) => r.data);
  },
  incidents(id: string) {
    return api.get<IncidentEvent[]>(`/patrols/${id}/incidents`).then((r) => r.data);
  },
};

// ── Admin ──────────────────────────────────────────────────────────────────────

export const adminApi = {
  // Guards
  listGuards() {
    return api.get<Guard[]>('/admin/guards').then((r) => r.data);
  },
  createGuard(data: { display_name: string; pin: string }) {
    return api.post<Guard>('/admin/guards', data).then((r) => r.data);
  },
  updateGuard(id: string, data: { display_name?: string; pin?: string; is_active?: boolean }) {
    return api.patch<Guard>(`/admin/guards/${id}`, data).then((r) => r.data);
  },
  deleteGuard(id: string) {
    return api.delete(`/admin/guards/${id}`).then(() => undefined);
  },

  // Portal users
  listPortalUsers() {
    return api.get<PortalUser[]>('/admin/portal-users').then((r) => r.data);
  },
  createPortalUser(data: { email: string; display_name: string; role: string; password: string }) {
    return api.post<PortalUser>('/admin/portal-users', data).then((r) => r.data);
  },
  updatePortalUser(id: string, data: Partial<{ display_name: string; is_active: boolean; role: string; password: string }>) {
    return api.patch<PortalUser>(`/admin/portal-users/${id}`, data).then((r) => r.data);
  },
  deletePortalUser(id: string) {
    return api.delete(`/admin/portal-users/${id}`).then(() => undefined);
  },

  // Alarms
  listAlarms(page = 1, guardId?: string) {
    const params: Record<string, unknown> = { page, per_page: CONFIG.PAGINATION_PAGE_SIZE };
    if (guardId) params.guard_id = guardId;
    return api
      .get<Paginated<AlarmEvent>>('/admin/alarms', { params })
      .then((r) => r.data);
  },

  // Incidents (orange button events)
  listIncidents(page = 1, guardId?: string) {
    const params: Record<string, unknown> = { page, per_page: CONFIG.PAGINATION_PAGE_SIZE };
    if (guardId) params.guard_id = guardId;
    return api
      .get<Paginated<IncidentEvent>>('/incidents', { params })
      .then((r) => r.data);
  },

  // Active sessions snapshot
  activeSessions() {
    return api.get<LiveSession[]>('/admin/active-sessions').then((r) => r.data);
  },

  // Completed patrols' trails for the last N hours, downsampled server-side
  // (see pt_db:list_recent_tracks/4) — the faded backdrop on the live map.
  historyTracks(hours = 24) {
    return api
      .get<HistoryTrack[]>('/admin/history-tracks', { params: { hours } })
      .then((r) => r.data);
  },

  // Drones (live telemetry from argus — see backend/apps/argus/ARGUS.md)
  listDrones() {
    return api.get<Drone[]>('/admin/drones').then((r) => r.data);
  },

  // Estate settings
  updateSettings(data: Partial<Pick<Estate, 'alarm_contacts'>> & { name?: string; address?: string; default_lat?: number | null; default_lng?: number | null }) {
    return api.patch<Estate>('/admin/settings', data).then((r) => r.data);
  },
  getEstate() {
    return api.get<Estate>('/admin/settings').then((r) => r.data);
  },

  // Patrol types
  listPatrolTypes() {
    return api.get<PatrolType[]>('/patrol-types').then((r) => r.data);
  },
  createPatrolType(name: string) {
    return api.post<PatrolType>('/patrol-types', { name }).then((r) => r.data);
  },
  deletePatrolType(id: string) {
    return api.delete(`/patrol-types/${id}`).then(() => undefined);
  },
  setPatrolType(patrolId: string, patrolTypeId: string | null) {
    return api.patch(`/patrols/${patrolId}/type`, { patrol_type_id: patrolTypeId }).then(() => undefined);
  },

  // Subscription
  getSubscription() {
    return api
      .get<{ subscription_status: string; amount: string; cancellable: boolean }>('/admin/subscription')
      .then((r) => r.data);
  },
  cancelSubscription() {
    return api.post('/admin/subscription/cancel').then(() => undefined);
  },

  // Feedback
  sendFeedback(data: { category: string; message: string }) {
    return api.post<{ id: string }>('/feedback', data).then((r) => r.data);
  },

  // Reports
  monthlyReport(year: number, month: number) {
    return api.get<MonthlyReport>('/reports/monthly', { params: { year, month } }).then((r) => r.data);
  },
};

// ── System health ──────────────────────────────────────────────────────────────

export interface SystemHealth {
  status: string;
  uptime_s: number;
  process_count: number;
  process_limit: number;
  memory_mb: number;
  active_patrols: number;
  db_status: 'ok' | 'error';
}

// ── Portal-wide (all roles) ────────────────────────────────────────────────────

export const portalApi = {
  getEstate() {
    return api.get<Estate>('/estate').then((r) => r.data);
  },
  health() {
    return api.get<SystemHealth>('/health').then((r) => r.data);
  },
  listGuards() {
    return api.get<Guard[]>('/guards').then((r) => r.data);
  },
  listPortalUsers() {
    return api.get<PortalUser[]>('/portal-users').then((r) => r.data);
  },
  listAlarms(page = 1, guardId?: string) {
    const params: Record<string, unknown> = { page, per_page: CONFIG.PAGINATION_PAGE_SIZE };
    if (guardId) params.guard_id = guardId;
    return api
      .get<Paginated<AlarmEvent>>('/admin/alarms', { params })
      .then((r) => r.data);
  },
  listIncidents(page = 1, guardId?: string) {
    const params: Record<string, unknown> = { page, per_page: CONFIG.PAGINATION_PAGE_SIZE };
    if (guardId) params.guard_id = guardId;
    return api
      .get<Paginated<IncidentEvent>>('/incidents', { params })
      .then((r) => r.data);
  },
  activeSessions() {
    return api.get<LiveSession[]>('/admin/active-sessions').then((r) => r.data);
  },
};

// ── Public / registration ──────────────────────────────────────────────────────

export const publicApi = {
  /** Step 1 of signup: stash the details and get a PayFast form URL back.
   *  No account exists until payment clears — see pt_rest_handler's
   *  register_start / payfast_notify. */
  registerStart(data: {
    site_name: string;
    site_address: string;
    admin_email: string;
    admin_name: string;
    admin_password: string;
    /** Version of the terms the registrant ticked, stored against the site. */
    terms_version: string;
  }) {
    return api
      .post<{ claim_token: string; payment_url: string; amount: string }>('/register/start', data)
      .then((r) => r.data);
  },

  /** Polled by the success page: the ITN is asynchronous, so the buyer can
   *  arrive back before PayFast has told us anything. */
  registerStatus(token: string) {
    return api
      .get<{ status: string; site_name: string; email: string }>('/register/status', {
        params: { token },
      })
      .then((r) => r.data);
  },

  // register() removed 2026-09-22 along with POST /estates/register. It was the
  // pre-payment signup path, had no caller left here, and was unauthenticated
  // on the server. Onboarding a site by hand is now psql or a PayFast signup.
};

export default api;
